/**
 * Load prop sprites into MapLibre map for icon-image usage.
 * Browser-only utility - requires DOM Canvas API.
 */
import type { Map as MapLibreMap } from "maplibre-gl";

/** Layer sprite names — empty, non-nature scenery removed */
export const LAYER_SPRITE_NAMES: readonly string[] = [] as const;

/** Prop sprite names (unique props only - shared ones use layer-* sprites) */
export const PROP_SPRITE_NAMES = [
  "prop-tree",
  "prop-palm",
  "prop-pine",
  "prop-lamp",
  "prop-bush",
  "prop-rock",
] as const;

interface SpriteMetadata {
  x: number;
  y: number;
  width: number;
  height: number;
  pixelRatio: number;
}

/**
 * Load layer sprites into the map (currently no-op — no layer sprites defined).
 * Sprites are loaded from the layer-icons sprite sheet.
 *
 * @param map - MapLibre map instance
 * @param spriteBase - Base URL for sprite sheet (default: "/sprites/layer-icons")
 * @returns Promise that resolves when all sprites are loaded
 */
export async function loadLayerSprites(
  map: MapLibreMap,
  spriteBase: string = "/sprites/layer-icons"
): Promise<void> {
  // No layer sprites to load — skip network request
  if (LAYER_SPRITE_NAMES.length === 0) return;

  // Determine if we should use retina sprites
  const pixelRatio = window.devicePixelRatio > 1 ? 2 : 1;
  const suffix = pixelRatio === 2 ? "@2x" : "";
  const jsonUrl = `${spriteBase}${suffix}.json`;
  const pngUrl = `${spriteBase}${suffix}.png`;

  try {
    // Fetch sprite metadata
    const response = await fetch(jsonUrl);
    if (!response.ok) {
      console.warn(`Failed to fetch layer sprite metadata: ${jsonUrl}`);
      return;
    }
    const metadata: Record<string, SpriteMetadata> = await response.json();

    // Load sprite sheet image
    const spriteImage = await loadImage(pngUrl);

    // Create a canvas to extract individual sprites
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.warn("Failed to get canvas context for sprite extraction");
      return;
    }

    // Extract and add each sprite
    for (const spriteName of LAYER_SPRITE_NAMES) {
      const spriteData = metadata[spriteName];
      if (!spriteData) {
        console.warn(`Sprite metadata not found for: ${spriteName}`);
        continue;
      }

      // Skip if already loaded
      if (map.hasImage(spriteName)) continue;

      // Set canvas size to sprite size
      canvas.width = spriteData.width;
      canvas.height = spriteData.height;

      // Clear and draw the sprite portion
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        spriteImage,
        spriteData.x,
        spriteData.y,
        spriteData.width,
        spriteData.height,
        0,
        0,
        spriteData.width,
        spriteData.height
      );

      // Get image data and add to map
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      map.addImage(spriteName, imageData, { pixelRatio });
    }
  } catch (err) {
    console.warn("Failed to load layer sprites:", err);
  }
}

/**
 * Load all prop sprites into the map.
 * Sprites are loaded from the prop-icons sprite sheet.
 *
 * @param map - MapLibre map instance
 * @param spriteBase - Base URL for sprite sheet (default: "/sprites/prop-icons")
 * @returns Promise that resolves when all sprites are loaded
 */
export async function loadPropSprites(
  map: MapLibreMap,
  spriteBase: string = "/sprites/prop-icons"
): Promise<void> {
  // Determine if we should use retina sprites
  const pixelRatio = window.devicePixelRatio > 1 ? 2 : 1;
  const suffix = pixelRatio === 2 ? "@2x" : "";
  const jsonUrl = `${spriteBase}${suffix}.json`;
  const pngUrl = `${spriteBase}${suffix}.png`;

  try {
    // Fetch sprite metadata
    const response = await fetch(jsonUrl);
    if (!response.ok) {
      console.warn(`Failed to fetch prop sprite metadata: ${jsonUrl}`);
      return;
    }
    const metadata: Record<string, SpriteMetadata> = await response.json();

    // Load sprite sheet image
    const spriteImage = await loadImage(pngUrl);

    // Create a canvas to extract individual sprites
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.warn("Failed to get canvas context for sprite extraction");
      return;
    }

    // Extract and add each sprite
    for (const spriteName of PROP_SPRITE_NAMES) {
      const spriteData = metadata[spriteName];
      if (!spriteData) {
        console.warn(`Sprite metadata not found for: ${spriteName}`);
        continue;
      }

      // Skip if already loaded
      if (map.hasImage(spriteName)) continue;

      // Set canvas size to sprite size
      canvas.width = spriteData.width;
      canvas.height = spriteData.height;

      // Clear and draw the sprite portion
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        spriteImage,
        spriteData.x,
        spriteData.y,
        spriteData.width,
        spriteData.height,
        0,
        0,
        spriteData.width,
        spriteData.height
      );

      // Get image data and add to map
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      map.addImage(spriteName, imageData, { pixelRatio });
    }
  } catch (err) {
    console.warn("Failed to load prop sprites:", err);
  }
}

/**
 * Load an image from URL
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}
